import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@shoflo/polymer/components/Alert/Alert';
import { Button } from '@shoflo/polymer/components/Button/Button';
import { Input } from '@shoflo/polymer/components/Input/Input';
import { InputLabel } from '@shoflo/polymer/components/InputLabel/InputLabel';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { PasswordTooltip } from '@shoflo/polymer/components/PasswordTooltip/PasswordTooltip';
import ModalPage from 'containers/pages/ModalPage/ModalPage';
import { connect } from 'react-redux';

import AppPaths from '../../../AppPaths';
import { updatePasswordFromRecoveryToken } from 'actions/user';
import { updatePasswordStatuses } from 'constants/updatePasswordStatus';
import ModalFooterLink from '../../../components/Links/ModalFooterLink';

const ResetPasswordFooter = ({ token, isSuccessful }) => {
  if (!token) {
    return (
      <ModalFooterLink to={AppPaths.ForgotPasswordPage}>
        Go to forgot password page.
      </ModalFooterLink>
    );
  }
  if (isSuccessful) {
    return (
      <ModalFooterLink to={AppPaths.LoginPage}>
        Go back to sign in.
      </ModalFooterLink>
    );
  }
  return null;
};

ResetPasswordFooter.propTypes = {
  token: PropTypes.string,
  isSuccessful: PropTypes.bool,
};

export const UnconnectedResetPasswordPage = (props) => {
  const { completed } = updatePasswordStatuses;
  const { match, location, updatePasswordFromRecoveryToken, status } = props;

  const token = match?.params.token;
  const email = new URLSearchParams(location?.search).get('email');
  const isSuccessful = status === completed;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordState, setPasswordState] = useState({
    password: '',
    error: null,
  });

  const onSubmit = () => {
    setIsSubmitting(true);
    updatePasswordFromRecoveryToken(email, token, password);
  };

  const onChange = (e) => {
    const { value } = e.target;
    const isValid = UserValidator.password(value);

    const passwordState = {
      password: value,
      error: isValid
        ? null
        : {
            type: 'error',
            message: 'Uh oh! Password is invalid',
          },
    };
    setPasswordState(passwordState);
  };

  const { error, password } = passwordState;

  return (
    <ModalPage
      header="Reset password"
      actionButton={
        !isSuccessful &&
        token && (
          <Button
            onClick={onSubmit}
            color="primary"
            disabled={isSubmitting || password.length === 0 || error !== null}
            data-testid="reset-password-button"
          >
            Reset password
          </Button>
        )
      }
      footer={<ResetPasswordFooter token={token} isSuccessful={isSuccessful} />}
    >
      <>
        {isSuccessful && (
          <Alert
            type="success"
            title="Password reset"
            content="Your password has been succesfully reset and can now be used to sign in via the link below."
          />
        )}
        {!token && (
          <Alert
            type="failure"
            title="Unable to reset password"
            content="Your link may be expired.  You can request a new link on the forgot password page."
          />
        )}

        {token && !isSuccessful && (
          <>
            <InputLabel for="password">New Password</InputLabel>
            <Input
              id="password"
              name="password"
              placeholder="Enter new password"
              type="password"
              data-testid="password-input"
              value={password}
              error={error}
              onChange={onChange}
            />
            {error !== null && (
              <PasswordTooltip
                password={password}
                targetElement={'password'}
                placement="top-end"
              />
            )}
          </>
        )}
      </>
    </ModalPage>
  );
};

UnconnectedResetPasswordPage.propTypes = {
  match: PropTypes.object,
  location: PropTypes.object,
  updatePasswordFromRecoveryToken: PropTypes.func,
  status: PropTypes.string,
};

export default connect(
  ({ updatePasswordStatus }) => ({ status: updatePasswordStatus }),
  { updatePasswordFromRecoveryToken },
)(UnconnectedResetPasswordPage);
